'use client';
import React, { useState, useEffect, useContext } from 'react';
import styles from './available-sections/styles/AvailableSections.module.css';
import { UserContext } from '../context/UserContext';
import EnrollmentModals from './available-sections/EnrollmentModals/EnrollmentModals';
import AuthModals from './auth/AuthModals';

import SectionCard from './available-sections/SectionCard/SectionCard';
import AddSectionModal from './course-details/Modals/AddSectionModal';
import RequestTimeModal from './RequestTimeModal';

import { getActualStartDate } from './available-sections/SectionHelpers';

export default function AvailableSections({ courseId, course }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = useContext(UserContext);
  const userRole = user?.role || null;
  const [meetingsVisible, setMeetingsVisible] = useState({});
  const [seatsInputs, setSeatsInputs] = useState({});

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showEnrollmentModals, setShowEnrollmentModals] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);

  // Edit section modal state
  const [editSectionModalOpen, setEditSectionModalOpen] = useState(false);
  const [sectionToEdit, setSectionToEdit] = useState(null);

  // Request time modal state
  const [showRequestTimeModal, setShowRequestTimeModal] = useState(false);

  useEffect(() => {
    const loadSections = async () => {
      try {
        setLoading(true);
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await fetch(
          `/api/classes/${courseId}/sections?timeZone=${encodeURIComponent(userTimeZone)}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        // Sort sections by first meeting time for consistent chronological order
        const sortedData = data.sort((a, b) => 
          getActualStartDate(a) - getActualStartDate(b)
        );
        setSections(sortedData);
      } catch (error) {
        console.error('Error loading sections:', error);
      } finally {
        setLoading(false);
      }
    };
    if (courseId) loadSections();
  }, [courseId]);

  const refreshSections = async () => {
    try {
      setLoading(true);
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await fetch(
        `/api/classes/${courseId}/sections?timeZone=${encodeURIComponent(userTimeZone)}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setSections(data);
    } catch (err) {
      console.error('Error refreshing sections:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if user can see all sections (including completed/past)
  const canSeeAllSections = () => {
    if (!user) return false;
    if (['ADMIN', 'SUPER_ADMIN'].includes(user.role)) return true;
    if (user.isTestingParent === true) return true;
    // Check if user is the course teacher
    const courseTeacherId = course?.teacherId || sections[0]?.course?.teacherId;
    if (user.role === 'TEACHER' && courseTeacherId === user.id) return true;
    return false;
  };

  // Filter sections based on user permissions
  const showAll = canSeeAllSections();
  let displayedSections;
  if (showAll) {
    // Show all sections with completed/past sections first
    const completedSections = sections.filter(sec => new Date() >= new Date(sec.endDate));
    const activeSections = sections.filter(sec => new Date() < new Date(sec.endDate));
    displayedSections = [...completedSections, ...activeSections];
  } else {
    // Regular users only see sections that haven't ended
    displayedSections = sections.filter(sec => new Date() < new Date(sec.endDate));
  }

  const handleEnrollClick = (section) => {
    if (!user) {
      setShowAuthModal(true);
    } else if (userRole === 'USER') {
      setSelectedSection(section);
      setShowEnrollmentModals(true);
    } else {
      alert('Enrollment only available to user accounts.');
    }
  };

  const toggleMeetings = (sectionId) => {
    setMeetingsVisible((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleSeatsInputChange = (sectionId, value) => {
    setSeatsInputs(prev => ({ ...prev, [sectionId]: value }));
  };

  const handleUpdateSeats = async (sectionId) => {
    const newSeatsTotal = seatsInputs[sectionId];
    if (!newSeatsTotal) return;

    try {
      const res = await fetch(`/api/classes/sections/${sectionId}/seats`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seatsTotal: parseInt(newSeatsTotal, 10) }),
      });
      if (res.ok) {
        refreshSections();
        setSeatsInputs(prev => {
          const updated = { ...prev };
          delete updated[sectionId];
          return updated;
        });
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to update seats');
      }
    } catch (err) {
      console.error('Failed to update seats:', err);
    }
  };

  const handleEditSection = (section) => {
    setSectionToEdit(section);
    setEditSectionModalOpen(true);
  };

  const handleUpdateSection = async (sectionData) => {
    try {
      const response = await fetch(`/api/classes/sections/${sectionToEdit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(sectionData),
      });
      
      if (!response.ok) throw new Error('Failed to update section');
      
      const responseData = await response.json();
      const updatedSection = responseData.section;
      
      // Update the sections state with the updated section
      setSections((prev) => {
        const updated = prev.map((sec) =>
          sec.id === sectionToEdit.id ? updatedSection : sec
        );
        // Sort sections by first meeting time to maintain chronological order
        return updated.sort((a, b) => 
          getActualStartDate(a) - getActualStartDate(b)
        );
      });
      
      // Close modal and reset state
      setEditSectionModalOpen(false);
      setSectionToEdit(null);
      
      alert('Section updated successfully');
    } catch (error) {
      console.error('Error updating section:', error);
      alert('Failed to update section');
    }
  }



  const handleDeleteSection = async (sectionId) => {
    if (!confirm('Are you sure you want to delete this section?')) return;
    try {
      const response = await fetch(`/api/classes/sections/${sectionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to delete section');
      setSections((prev) => prev.filter((s) => s.id !== sectionId));
      alert('Section deleted successfully');
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to delete section');
    }
  };

  return (
    <div className={styles.availableTimesSection}>
      <h2>Currently Offered Sections</h2>
      {loading ? (
        <p>Loading available classes...</p>
      ) : displayedSections.length === 0 ? (
        <p>No currently offered sections for this course.</p>
      ) : (
        <div className={styles.availableTimesPage}>
          {displayedSections.map((section, index) => (
            <SectionCard
              key={section.id || `section-${index}`}
              section={section}
              user={user}
              meetingsVisible={meetingsVisible}
              onToggleMeetings={toggleMeetings}
              onEditSection={handleEditSection}
              onDeleteSection={handleDeleteSection}
              onEnrollClick={handleEnrollClick}
              seatsInputs={seatsInputs}
              onSeatsInputChange={handleSeatsInputChange}
              onUpdateSeats={handleUpdateSeats}
            />
          ))}
        </div>
      )}

      {/* Show for parents (USER role) and guests (not logged in) */}
      {(!user || user.role === 'USER') && (
        <div className={styles.requestTimeContainer}>
          <button
            onClick={() => {
              if (!user) {
                setShowAuthModal(true);
              } else {
                setShowRequestTimeModal(true);
              }
            }}
            className={styles.requestTimeButton}
          >
            Don&apos;t see a time that works? Request it
          </button>
        </div>
      )}

      <AuthModals isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      {selectedSection && (() => {
        const allCourseEnrolledLearnerIds = [...new Set(
          sections.flatMap(s => (s.enrolledLearners || []).map(l => l.id))
        )];
        return (
          <EnrollmentModals
            isOpen={showEnrollmentModals}
            onClose={() => setShowEnrollmentModals(false)}
            userRole={userRole}
            course={course}
            section={selectedSection}
            onEnrollmentSuccess={refreshSections}
            allCourseEnrolledLearnerIds={allCourseEnrolledLearnerIds}
          />
        );
      })()}

      {sectionToEdit && (
        <AddSectionModal
          isOpen={editSectionModalOpen}
          onClose={() => {
            setEditSectionModalOpen(false);
            setSectionToEdit(null);
          }}
          onSave={handleUpdateSection}
          courseId={courseId}
          editMode={true}
          initialData={sectionToEdit}
          sectionId={sectionToEdit.id}
          userRole={user?.role}
          sidebarHints={{
            weeklyFrequency: course?.sidebar?.weeklyFrequency || 1,
            courseDurationWeeks: course?.sidebar?.courseDuration || 10,
            singleClassDurationMinutes: course?.sidebar?.singleClassDuration || 60,
          }}
        />
      )}

      <RequestTimeModal
        isOpen={showRequestTimeModal}
        onClose={() => setShowRequestTimeModal(false)}
        courseName={course?.name}
        courseId={course?.id}
        teacherName={course?.teacher?.name}
        teacherEmail={course?.teacher?.email}
      />
    </div>
  );
}
